/**
 * Documentos. Zona de arrastrar y soltar + tabla paginada.
 *
 * La subida real de ficheros y el OCR son fase 2: la zona reproduce el marcador del maquetado
 * y no acepta ficheros. El filtro por categoría sí funciona: es lectura, y la columna existe.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { DocumentCategory, DocumentListRow } from '@naar/shared';
import { api, queryKeys } from '../api/client';
import { PageHeader } from '../components/PageHeader';
import { Card } from '../components/Card';
import { Icon } from '../components/Icon';
import { DocumentThumb } from '../components/Avatar';
import { useDocumentViewer, ViewDocumentButton } from '../components/overlay/DocumentViewer';
import { DataTable, IdentityCell, TablePagination, cellStyles } from '../components/DataTable';
import { EmptyState, ErrorState } from '../components/EmptyState';
import { SkeletonCard, SkeletonPageHeader } from '../components/Skeleton';
import { formatShortDate } from '../lib/format';
import styles from './DocumentsPage.module.css';

/** Plantilla de columnas literal del maquetado. */
const TEMPLATE = '2.2fr 1.3fr 1.3fr 1fr 48px';

const COLUMNS = [
  { key: 'name', label: 'Nombre' },
  { key: 'category', label: 'Categoría' },
  { key: 'property', label: 'Propiedad' },
  { key: 'date', label: 'Fecha', align: 'right' as const },
  // El ojo: abre el documento en el visor (la fila entera también lo hace).
  { key: 'ver', label: '', align: 'right' as const },
];

const CATEGORIES: DocumentCategory[] = [
  'Contratos',
  'Inmueble',
  'Reparaciones',
  'Fiscal',
  'Nominas',
  'Identidad',
  'Pasaporte',
  'Auditoria',
  'Otros',
];

/** Cómo se lee cada categoría: el enum no lleva tildes ni espacios. */
const CATEGORY_LABELS: Partial<Record<DocumentCategory, string>> = {
  Nominas: 'Nóminas',
  Auditoria: 'Registro de auditoría',
};

export function DocumentsPage() {
  const visor = useDocumentViewer();
  const [page, setPage] = useState(1);
  const [category, setCategory] = useState<DocumentCategory | undefined>(undefined);

  const params = { page, limit: 25, ...(category ? { category } : {}) };
  const { data, isPending, error, refetch } = useQuery({
    queryKey: queryKeys.documents(params),
    queryFn: () => api.documents(params),
  });

  const header = (
    <PageHeader
      title="Documentos"
      subtitle="Todos los documentos de tu negocio inmobiliario, organizados y con OCR."
    />
  );

  const dropzone = (
    <div className={styles.dropzone}>
      <div className={styles.dropIcon}>
        <Icon name="upload" size={30} strokeWidth={1.8} />
      </div>
      <div className={styles.dropTitle}>Arrastra tus documentos aquí</div>
      <div className={styles.dropHint}>
        o reenvíalos a documentos@naar.es · la IA los clasifica solo
      </div>
    </div>
  );

  if (isPending) {
    return (
      <section>
        <SkeletonPageHeader />
        <SkeletonCard rows={1} height={110} />
        <div style={{ height: 18 }} />
        <SkeletonCard rows={4} height={44} />
      </section>
    );
  }

  if (error) {
    return (
      <section>
        {header}
        {dropzone}
        <ErrorState error={error} onRetry={() => void refetch()} />
      </section>
    );
  }

  return (
    <section>
      {header}
      {dropzone}

      <Card variant="table">
        <div className={styles.filters}>
          <button
            type="button"
            className={category === undefined ? `${styles.chip} ${styles.chipActive}` : styles.chip}
            onClick={() => {
              setCategory(undefined);
              setPage(1);
            }}
          >
            Todas
          </button>
          {CATEGORIES.map((item) => (
            <button
              key={item}
              type="button"
              className={category === item ? `${styles.chip} ${styles.chipActive}` : styles.chip}
              onClick={() => {
                setCategory(item);
                setPage(1);
              }}
            >
              {CATEGORY_LABELS[item] ?? item}
            </button>
          ))}
        </div>

        {data.data.length === 0 ? (
          <EmptyState
            inline
            icon="file"
            title="No hay documentos en esta categoría"
            body="Prueba con otra categoría o sube documentos nuevos."
          />
        ) : (
          <>
            <DataTable<DocumentListRow>
              caption="Documentos"
              columns={COLUMNS}
              template={TEMPLATE}
              rows={data.data}
              rowKey={(row) => row.id}
              // Pulsar un documento lo abre en el visor: se ve sin descargarlo.
              onRowClick={(row) =>
                visor.open({
                  id: row.id,
                  name: row.name,
                  meta: [
                    CATEGORY_LABELS[row.category] ?? row.category,
                    row.propertyName,
                    formatShortDate(row.issuedAt ?? row.uploadedAt),
                  ]
                    .filter(Boolean)
                    .join(' · '),
                })
              }
              renderRow={(row) => (
                <>
                  <IdentityCell size="sm" visual={<DocumentThumb />} main={row.name} />
                  <span className={cellStyles.cellMuted} style={{ color: 'var(--naar-text)' }}>
                    {CATEGORY_LABELS[row.category] ?? row.category}
                  </span>
                  <span className={cellStyles.cellSecondary}>{row.propertyName ?? '—'}</span>
                  <span className={`${cellStyles.cellMuted} ${cellStyles.right}`}>
                    {formatShortDate(row.issuedAt ?? row.uploadedAt)}
                  </span>
                  <span className={cellStyles.rightSelf}>
                    <ViewDocumentButton document={{ id: row.id, name: row.name }} />
                  </span>
                </>
              )}
              /* Móvil: el nombre entero (suele ser largo) y, debajo, categoría, inmueble y fecha. */
              renderCard={(row) => (
                <div className={cellStyles.cardHead}>
                  <IdentityCell
                    size="sm"
                    visual={<DocumentThumb />}
                    main={row.name}
                    sub={[
                      CATEGORY_LABELS[row.category] ?? row.category,
                      row.propertyName,
                      formatShortDate(row.issuedAt ?? row.uploadedAt),
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  />
                  <ViewDocumentButton document={{ id: row.id, name: row.name }} />
                </div>
              )}
            />
            <TablePagination
              page={data.meta.page}
              totalPages={data.meta.totalPages}
              total={data.meta.total}
              onChange={setPage}
              label="documentos"
            />
          </>
        )}
      </Card>
    </section>
  );
}
